// Firestore-backed drop-in replacement for the old axios API client.
// Exposes an `api` object with get/post/put/delete that returns { data },
// so existing pages keep working unchanged.

import { db, auth, ADMIN_EMAIL } from "./firebase";
import {
  collection, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, deleteDoc,
  serverTimestamp, arrayUnion, arrayRemove,
} from "firebase/firestore";
import { uploadToR2, fileUrl } from "./storage";

// Kept for backward-compat imports.
export const API = "";
export const FILE_URL = (idOrUrl) => fileUrl(idOrUrl);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Xəta baş verdi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function httpError(status, detail) {
  const e = new Error(typeof detail === "string" ? detail : "Error");
  e.response = { status, data: { detail } };
  return e;
}

// ---------- auth helpers ----------
// Client-side admin flag, kept in sync by the auth provider (based on the user's
// Firestore role). Real enforcement is in Firestore security rules.
let _clientAdmin = false;
export function setClientAdmin(v) { _clientAdmin = !!v; }
export function isAdminUser() {
  const u = auth.currentUser;
  if (u && (u.email || "").toLowerCase() === ADMIN_EMAIL) return true; // super admin
  return !!(u && _clientAdmin);
}
function requireAdmin() { if (!isAdminUser()) throw httpError(403, "Yalnız admin"); }
function requireUser() {
  const u = auth.currentUser;
  if (!u) throw httpError(401, "Daxil olmamısınız");
  return u;
}

// ---------- user doc ----------
export async function ensureUserDoc(fbUser, extra = {}) {
  const ref = doc(db, "users", fbUser.uid);
  const snap = await getDoc(ref);
  const email = (fbUser.email || "").toLowerCase();
  if (!snap.exists()) {
    // New users are always created as "user". Promote to admin from the
    // Firestore console (or as super admin) by setting role = "admin".
    const data = {
      uid: fbUser.uid, email,
      name: extra.name || "", surname: extra.surname || "",
      role: "user", favorites: [], cart: [], createdAt: serverTimestamp(),
    };
    await setDoc(ref, data);
    return data;
  }
  return snap.data();
}

export async function getUserData() {
  const u = requireUser();
  const snap = await getDoc(doc(db, "users", u.uid));
  return snap.exists() ? snap.data() : ensureUserDoc(u);
}

export async function getMe() {
  const u = auth.currentUser;
  if (!u) throw httpError(401, "Daxil olmamısınız");
  const d = await getUserData();
  const isSuper = (u.email || "").toLowerCase() === ADMIN_EMAIL;
  const role = isSuper ? "admin" : (d.role || "user");
  return {
    id: u.uid, email: d.email || u.email, name: d.name || "", surname: d.surname || "",
    role, favorites: d.favorites || [], cart: d.cart || [],
  };
}

// ---------- doors ----------
function tsMillis(v) {
  if (!v) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v.seconds) return v.seconds * 1000;
  return 0;
}
const byCreatedDesc = (a, b) => tsMillis(b.createdAt) - tsMillis(a.createdAt);

async function listDoors(category) {
  const snap = await getDocs(collection(db, "doors"));
  let docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  docs.sort(byCreatedDesc);
  if (category && category !== "all") docs = docs.filter((d) => d.category === category);
  return docs;
}
async function getDoorById(id) {
  const snap = await getDoc(doc(db, "doors", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
function normalizeDoor(body) {
  return {
    name: body.name || { az: "", ru: "", en: "" },
    description: body.description || { az: "", ru: "", en: "" },
    price: Number(body.price) || 0,
    category: body.category || "entrance",
    material: body.material || "",
    dimensions: body.dimensions || "",
    colors: Array.isArray(body.colors) ? body.colors : [],
    image_id: body.image_id || null,
  };
}

// ---------- catalogs ----------
async function listCatalogs() {
  const snap = await getDocs(collection(db, "catalogs"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byCreatedDesc);
}

// ---------- settings ----------
async function getSettings() {
  const snap = await getDoc(doc(db, "settings", "main"));
  return snap.exists() ? { id: "main", ...snap.data() } : { id: "main" };
}

// ---------- favorites ----------
export async function addFavorite(doorId) {
  const u = requireUser();
  await updateDoc(doc(db, "users", u.uid), { favorites: arrayUnion(doorId) });
}
export async function removeFavorite(doorId) {
  const u = requireUser();
  await updateDoc(doc(db, "users", u.uid), { favorites: arrayRemove(doorId) });
}
async function getMyFavoriteDoors() {
  const d = await getUserData();
  const ids = d.favorites || [];
  if (!ids.length) return [];
  const all = await listDoors();
  const byId = Object.fromEntries(all.map((x) => [x.id, x]));
  return ids.map((id) => byId[id]).filter(Boolean);
}

// ---------- cart ----------
export async function upsertCart(doorId, qty) {
  const u = requireUser();
  const ref = doc(db, "users", u.uid);
  const snap = await getDoc(ref);
  const cart = (snap.data() && snap.data().cart) || [];
  const q = Math.max(1, parseInt(qty, 10) || 1);
  const idx = cart.findIndex((c) => c.door_id === doorId);
  if (idx >= 0) cart[idx] = { door_id: doorId, qty: q };
  else cart.push({ door_id: doorId, qty: q });
  await updateDoc(ref, { cart });
}
export async function removeCart(doorId) {
  const u = requireUser();
  const ref = doc(db, "users", u.uid);
  const snap = await getDoc(ref);
  const cart = (((snap.data() && snap.data().cart) || [])).filter((c) => c.door_id !== doorId);
  await updateDoc(ref, { cart });
}
async function getMyCart() {
  const d = await getUserData();
  const items = d.cart || [];
  if (!items.length) return [];
  const all = await listDoors();
  const byId = Object.fromEntries(all.map((x) => [x.id, x]));
  return items.map((it) => ({ door: byId[it.door_id], qty: Math.max(1, it.qty || 1) })).filter((x) => x.door);
}

export async function syncGuest(body) {
  const u = requireUser();
  const ref = doc(db, "users", u.uid);
  const snap = await getDoc(ref);
  const cur = snap.data() || {};
  const update = {};
  if (body && body.favorites) {
    update.favorites = Array.from(new Set([...(cur.favorites || []), ...body.favorites]));
  }
  if (body && body.cart) {
    const map = {};
    (cur.cart || []).forEach((c) => { map[c.door_id] = Math.max(1, c.qty || 1); });
    body.cart.forEach((c) => { map[c.door_id] = Math.max(1, c.qty || 1, map[c.door_id] || 0); });
    update.cart = Object.entries(map).map(([door_id, qty]) => ({ door_id, qty }));
  }
  if (Object.keys(update).length) await updateDoc(ref, update);
  return getMe();
}

// ---------- files ----------
async function uploadFile(fileOrForm) {
  const file = (typeof FormData !== "undefined" && fileOrForm instanceof FormData)
    ? fileOrForm.get("file") : fileOrForm;
  if (!file) throw httpError(400, "Fayl yoxdur");
  const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
  const { url, key } = await uploadToR2(file, { idToken });
  const value = url || key;
  return { id: value, file_id: value, url: value, key };
}

// ---------- router ----------
function splitPath(path) {
  const [p, qs] = String(path).split("?");
  return [p.replace(/\/$/, "") || "/", new URLSearchParams(qs || "")];
}

async function get(path) {
  const [p, params] = splitPath(path);
  if (p === "/") return { data: { message: "ok" } };
  if (p === "/doors") return { data: await listDoors(params.get("category")) };
  if (p.startsWith("/doors/")) {
    const d = await getDoorById(p.slice("/doors/".length));
    if (!d) throw httpError(404, "Qapı tapılmadı");
    return { data: d };
  }
  if (p === "/catalogs") return { data: await listCatalogs() };
  if (p === "/settings") return { data: await getSettings() };
  if (p === "/me/favorites") return { data: await getMyFavoriteDoors() };
  if (p === "/me/cart") return { data: await getMyCart() };
  if (p === "/auth/me") return { data: await getMe() };
  throw httpError(404, "Tapılmadı: " + p);
}

async function post(path, body) {
  const [p] = splitPath(path);
  if (p === "/files/upload") return { data: await uploadFile(body) };
  if (p === "/doors") {
    requireAdmin();
    const ref = await addDoc(collection(db, "doors"), { ...normalizeDoor(body), createdAt: serverTimestamp() });
    return { data: { id: ref.id, ...normalizeDoor(body) } };
  }
  if (p === "/catalogs") {
    requireAdmin();
    const payload = { title: body.title || { az: "", ru: "", en: "" }, file_id: body.file_id || null, cover_id: body.cover_id || null };
    const ref = await addDoc(collection(db, "catalogs"), { ...payload, createdAt: serverTimestamp() });
    return { data: { id: ref.id, ...payload } };
  }
  if (p.startsWith("/me/favorites/")) { await addFavorite(p.slice("/me/favorites/".length)); return { data: { ok: true } }; }
  if (p === "/me/cart") { await upsertCart(body.door_id, body.qty); return { data: { ok: true } }; }
  if (p === "/me/sync") { return { data: await syncGuest(body) }; }
  throw httpError(404, "Tapılmadı: " + p);
}

async function put(path, body) {
  const [p] = splitPath(path);
  if (p.startsWith("/doors/")) {
    requireAdmin();
    const id = p.slice("/doors/".length);
    const patch = {}; const keys = ["name", "description", "price", "category", "material", "dimensions", "colors", "image_id"];
    keys.forEach((k) => { if (body[k] !== undefined) patch[k] = k === "price" ? (Number(body[k]) || 0) : body[k]; });
    await updateDoc(doc(db, "doors", id), patch);
    return { data: { id, ...patch } };
  }
  if (p.startsWith("/catalogs/")) {
    requireAdmin();
    const id = p.slice("/catalogs/".length);
    const patch = {}; const keys = ["title", "file_id", "cover_id"];
    keys.forEach((k) => { if (body[k] !== undefined) patch[k] = body[k]; });
    await updateDoc(doc(db, "catalogs", id), patch);
    return { data: { id, ...patch } };
  }
  if (p === "/settings") {
    requireAdmin();
    await setDoc(doc(db, "settings", "main"), { ...body }, { merge: true });
    return { data: await getSettings() };
  }
  throw httpError(404, "Tapılmadı: " + p);
}

async function del(path) {
  const [p] = splitPath(path);
  if (p.startsWith("/doors/")) { requireAdmin(); await deleteDoc(doc(db, "doors", p.slice("/doors/".length))); return { data: { ok: true } }; }
  if (p.startsWith("/catalogs/")) { requireAdmin(); await deleteDoc(doc(db, "catalogs", p.slice("/catalogs/".length))); return { data: { ok: true } }; }
  if (p.startsWith("/me/favorites/")) { await removeFavorite(p.slice("/me/favorites/".length)); return { data: { ok: true } }; }
  if (p.startsWith("/me/cart/")) { await removeCart(p.slice("/me/cart/".length)); return { data: { ok: true } }; }
  throw httpError(404, "Tapılmadı: " + p);
}

export const api = { get, post, put, delete: del };
